import { Link as MaterialLink } from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import { makeStyles } from '@material-ui/styles';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import venturesAPI, { VentureMentorHistory } from '../../api/ventures';
import { VentureId } from '../../api/ventures/types/Venture';
import { AlertState, PageLoader, Text, Link } from '../../components/common';
import PopupIcon from '../../components/common/popup-icon';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { formatDate } from '../../utils/date';
import { isTablet } from '../../utils/functions';

const useStyles = makeStyles({
  loader: {
    width: '100%',
    minHeight: 400,
    maxHeight: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: { width: '100%' },
  row: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    padding: '11px 12px 11px 0',
    '&:hover:not($headRow)': { background: COLORS.COLOR_GRAY_LIGHTENED_45 },
  },
  headRow: {
    padding: '13px 12px 15px 0',
    '& $leadTd': { padding: '0 16px' },
  },
  td: { padding: '0 10px', boxSizing: 'border-box' },
  dateTd: { minWidth: 140 },
  nameTd: {
    width: '100%',
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    overflow: 'hidden',
  },
  text: { color: COLORS.COLOR_BLUE_DARKENED_10 },
  link: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

function VentureMentorHistoryPage() {
  const classes = useStyles();
  const location = useLocation();
  const { ventureId } = useParams<{ ventureId: VentureId }>();

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [historeyMentors, setHistoreyMentors] = useState<
    VentureMentorHistory[]
  >([]);

  const currentPath = useMemo(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search],
  );

  useEffect(() => {
    async function loadHistoryMentors() {
      try {
        if (!ventureId) {
          throw new Error('Venture ID is required');
        }

        setIsLoading(true);
        const mentors = await venturesAPI.getHistoryMentors(ventureId);
        setHistoreyMentors(mentors);
      } catch (error: any) {
        console.error(error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Internal server error';
        setLoadError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }

    loadHistoryMentors();
  }, [ventureId]);

  const stopPropagation = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  if (isLoading) {
    return (
      <div className={classes.loader}>
        <PageLoader />
      </div>
    );
  }

  if (!!loadError) {
    return (
      <div className={classes.loader}>
        <AlertState type='error'>{loadError}</AlertState>
      </div>
    );
  }

  if (historeyMentors.length === 0) {
    return (
      <div className={classes.loader}>
        <Text variant='normal'>No assignments history yet...</Text>
      </div>
    );
  }

  return (
    <div className={cn(classes.container)}>
      <div className={cn(classes.row, classes.headRow)}>
        <div className={cn(classes.td, classes.nameTd)}>
          <Text variant='normal2' bold>
            Name
          </Text>
        </div>
        {!isTablet() && (
          <>
            <div className={cn(classes.td, classes.dateTd)}>
              <Text variant='normal2' bold>
                Start Date
              </Text>
            </div>
            <div className={cn(classes.td, classes.dateTd)}>
              <Text variant='normal2' bold>
                End Date
              </Text>
            </div>
          </>
        )}
      </div>

      {historeyMentors.map((mentor, index) => {
        const {
          id,
          mentorFirstName,
          mentorLastName,
          startDate,
          endDate,
          notes,
          mentorId,
        } = mentor;
        const name = [mentorFirstName, mentorLastName]
          .filter(Boolean)
          .join(' ');

        return (
          <div
            key={id}
            data-testid={`assigned-mentor-${index}`}
            className={classes.row}>
            <div className={cn(classes.td, classes.nameTd)}>
              {name ? (
                <>
                  <MaterialLink
                    onClick={stopPropagation}
                    component={Link}
                    className={classes.link}
                    to={{
                      pathname: getRoutePath(Pages.MENTOR_DETAILS, {
                        mentorId,
                      }),
                      state: { prevPath: currentPath },
                    }}>
                    <Text className={classes.text} variant='normal'>
                      {name}
                    </Text>
                  </MaterialLink>
                  {!!notes && (
                    <PopupIcon
                      icon={<AssignmentIcon />}
                      content={notes}
                      testId='notes-icon'
                      isNoIcon={!notes}
                      emptyText='There is no notes available.'
                      enterTouchDelay={0}
                    />
                  )}
                </>
              ) : (
                <Text variant='normal' color={COLORS.COLOR_RED_BASE}>
                  Deleted
                </Text>
              )}
            </div>
            {!isTablet() && (
              <>
                <div className={cn(classes.td, classes.dateTd)}>
                  <Text variant='normal'>
                    {startDate ? formatDate(startDate) : ''}
                  </Text>
                </div>
                <div className={cn(classes.td, classes.dateTd)}>
                  <Text variant='normal'>
                    {endDate ? formatDate(endDate) : ''}
                  </Text>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default VentureMentorHistoryPage;
